import React, { useState } from 'react';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Tooltip from 'components/Tooltip';
import {
  EntertainmentVideoAudioType,
  EntertainmentVideoItem,
  EntertainmentVideoItemBlank,
  FullEntertainmentVideo
} from 'modules/entertainment-videos/types';
import { __t, applyTranslations } from 'utils/language';
import { EntertainmentVideoSection } from '../entertainment-video-section';
import { EntertainmentVideoAudio } from '../entertainment-video-audio';
import { EntertainmentVideoCountries } from '../entertainment-video-countries';
import { EntertainmentVideoGenders } from '../entertainment-video-genders';
import { EntertainmentVideoMedia } from '../entertainment-video-media';
import { EntertainmentVideoProductReviewToggle } from '../entertainment-video-product-review-toggle';
import { EntertainmentVideoTags } from '../entertainment-video-tags';
import { EntertainmentVideoTimeframe } from '../entertainment-video-timeframe';
import { EntertainmentVideoItems } from '../entertainment-video-items';
import { FormProvider, useFormContext } from './FormProvider';
import { useDispatch, useSelector } from 'react-redux';
import { isUpdatingSelector } from 'selectors/entertainment-video';
import { API } from 'utils/api';
import { UserException } from 'utils/file';
import { danger } from 'redux-store/reducers/common-reducer';
import { uploadFile as uploadFileToS3 } from 'utils/upload-asset';

applyTranslations(require('./entertainment-video-edit-form.i18n.json'));

function translate(path: string, variables?: Record<string, any>) {
  return __t(`entertainmentVideoEditForm${path}`, variables);
}

type OnCancelFunction = (isFormModified: boolean) => void;
type OnSaveFunction = (data: {
  name: string,
  genders: Array<string>,
  tags: Array<string>,
  audioType: EntertainmentVideoAudioType | null,
  isProductReview: boolean,
  publishedFrom: string | null,
  publishedUntil: string | null,
  publishedCountries: Array<string>,
  videoUrl: string | null,
  thumbnailUrl: string | null,
  items: Array<EntertainmentVideoItem | EntertainmentVideoItemBlank>
}) => void;

export function EntertainmentVideoEditForm({
  fullEntertainmentVideo,
  onCancel,
  onSave
}: {
  fullEntertainmentVideo: FullEntertainmentVideo,
  onCancel: OnCancelFunction,
  onSave: OnSaveFunction
}) {
  return (
    <FormProvider fullEntertainmentVideo={fullEntertainmentVideo}>
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <EntertainmentVideoEditFormComponent
        fullEntertainmentVideo={fullEntertainmentVideo}
        onCancel={onCancel}
        onSave={onSave}
      />
    </FormProvider>
  );
}

function EntertainmentVideoEditFormComponent({
  fullEntertainmentVideo,
  onCancel,
  onSave
}: {
  fullEntertainmentVideo: FullEntertainmentVideo,
  onCancel: OnCancelFunction,
  onSave: OnSaveFunction
}) {
  const dispatch = useDispatch();

  const { media } = fullEntertainmentVideo;
  const { formValues, isModified, setFieldValue } = useFormContext();
  const [isUploading, setIsUploading] = useState(false);

  async function uploadFile(file: File, url: string): Promise<string> {
    const response = await uploadFileToS3(file, url);

    if (response.type === 'error') {
      throw new UserException(response.error, response.file);
    }

    const fileUrl = response.data.data.url;

    if (!fileUrl) {
      throw new Error('An error occurred, please try again later.');
    }

    return fileUrl;
  }

  async function handleUploadVideo(file: File): Promise<void> {
    if (isUploading) {
      return;
    }

    setIsUploading(true);

    try {
      const videoUrl = await uploadFile(file, API.END_POINTS.ENTERTAINMENT_VIDEOS.uploadEntertainmentVideoMedia());

      setFieldValue('videoUrl', videoUrl);
      // eslint-disable-next-line no-useless-catch
    } catch (err) {
      /**
       * This might look like a useless try-catch, but we need it to always set `isUploading` in `finally`,
       * no matter if the API called failed or not
       */
      throw err;
    } finally {
      setIsUploading(false);
    }
  }

  async function handleUploadThumbnail(file: File): Promise<void> {
    if (isUploading) {
      return;
    }

    setIsUploading(true);

    try {
      const thumbnailUrl = await uploadFile(file, API.END_POINTS.ENTERTAINMENT_VIDEOS.uploadEntertainmentVideoThumbnail());

      setFieldValue('thumbnailUrl', thumbnailUrl);
      // eslint-disable-next-line no-useless-catch
    } catch (err) {
      throw err;
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <div>
      {media && (
        <EntertainmentVideoSection>
          <EntertainmentVideoMedia
            isEditMode
            videoUrl={formValues.videoUrl}
            thumbnailUrl={formValues.thumbnailUrl}
            isUploading={isUploading}
            onUploadVideo={file => {
              handleUploadVideo(file);
            }}
            onDeleteThumbnail={() => {
              setFieldValue('thumbnailUrl', null);
            }}
            onUploadThumbnail={file => {
              handleUploadThumbnail(file);
            }}
          />
        </EntertainmentVideoSection>
      )}
      <EntertainmentVideoSection>
        <EntertainmentVideoProductReviewToggle
          isEditMode
          value={formValues.isProductReview}
          onChange={value => {
            setFieldValue('isProductReview', value);
          }}
        />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.audio')}>
        <EntertainmentVideoAudio
          isEditMode
          audioType={formValues.audioType}
          onTypeChange={newType => {
            setFieldValue('audioType', newType);
          }}
        />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.genders')}>
        <EntertainmentVideoGenders
          isEditMode
          genders={formValues.genders}
          onGendersChange={genders => {
            setFieldValue('genders', genders);
          }}
        />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.tags')}>
        <EntertainmentVideoTags
          isEditMode
          tags={formValues.tags}
          onTagsChange={tags => {
            setFieldValue('tags', tags);
          }}
        />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.countries')}>
        <EntertainmentVideoCountries
          isEditMode
          countries={formValues.publishedCountries}
          onCountriesChange={countries => {
            setFieldValue('publishedCountries', countries);
          }}
        />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.timeframe')}>
        <EntertainmentVideoTimeframe
          isEditMode
          publishedFrom={formValues.publishedFrom}
          publishedUntil={formValues.publishedUntil}
          onTimeframeChange={(from, until) => {
            setFieldValue('publishedFrom', from);
            setFieldValue('publishedUntil', until);
          }}
        />
      </EntertainmentVideoSection>
      <EntertainmentVideoSection title={translate('.sections.items')}>
        <EntertainmentVideoItems
          isEditMode
          items={formValues.items}
          onAddItem={newItem => {
            const hasItemWithSameSKU = formValues.items.some(item => item.sku === newItem.sku);

            if (hasItemWithSameSKU) {
              dispatch(
                danger(translate('.itemWithSameSKUAlreadyExists', { sku: newItem.sku }))
              );
              return;
            }

            setFieldValue('items', [...formValues.items, newItem]);
          }}
          onDeleteItem={deletedItem => {
            setFieldValue(
              'items',
              formValues.items.filter(item => item.sku !== deletedItem.sku)
            );
          }}
        />
      </EntertainmentVideoSection>
      <hr style={{ margin: '20px 0px' }} />
      <EntertainmentVideoSection narrow>
        {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
        <Actions
          isModified={isModified}
          onCancel={() => onCancel(isModified)}
          onSave={() =>
            onSave({
              name: fullEntertainmentVideo.entertainmentVideo.name,
              genders: formValues.genders,
              tags: formValues.tags,
              audioType: formValues.audioType === null ? null : EntertainmentVideoAudioType[formValues.audioType],
              isProductReview: formValues.isProductReview,
              publishedCountries: formValues.publishedCountries,
              publishedFrom: formValues.publishedFrom,
              publishedUntil: formValues.publishedUntil,
              videoUrl: formValues.videoUrl,
              thumbnailUrl: formValues.thumbnailUrl,
              items: formValues.items
            })
          }
        />
      </EntertainmentVideoSection>
    </div>
  );
}

function Actions({
  isModified,
  validationError,
  onCancel,
  onSave
}: {
  isModified: boolean,
  validationError?: any,
  onCancel: () => void,
  onSave: () => void
}) {
  const isUpdating = useSelector(isUpdatingSelector);

  let tooltipMsg: string | null = null;

  if (!isModified) {
    tooltipMsg = translate('.noChanges');
  }

  if (validationError) {
    tooltipMsg = 'TODO: validation error!';
  }

  const saveButton = (
    <ActionButton
      color="primary"
      disabled={!isModified || !!validationError || isUpdating}
      disabledWithSpinner={isUpdating}
      onClick={onSave}
    >
      {translate('.actions.save')}
    </ActionButton>
  );

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={{ marginRight: 24 }}>
        <ActionButton color="tertiary" onClick={onCancel}>
          {translate('.actions.cancel')}
        </ActionButton>
      </div>
      {tooltipMsg !== null ? (
        <Tooltip
          message={tooltipMsg}
          classNameWrapper="realm-outfit-details-edit__tooltip"
        >
          {saveButton}
        </Tooltip>
      ) : (
        saveButton
      )}
    </div>
  );
}
